import { useState, useEffect, useCallback, useMemo } from 'react';
import { Loader2, CalendarClock, ClipboardList, Flag, Check, Ban } from 'lucide-react';
import { arDunningApi } from '../../services/arDunningApi';
import { useToast } from '../ui/Toast';
import { formatMoney, formatDate, todayIso } from '../../utils/arDunningFormat';
import type { ArOpenItem, ArAction, ArActionStatus } from '../../types/arDunning';

interface Props {
    items: ArOpenItem[];
    canEdit: boolean;
    reloadKey: number;
    /** Focus a sub-tab from outside (e.g. a KPI tile); applied when focusNonce changes. */
    focusSub?: SubTab;
    focusNonce?: number;
    onOpenDetail: (item: ArOpenItem) => void;
    onChanged: () => void;
}

type SubTab = 'mine' | 'promises' | 'disputes';

const SUBTABS: { key: SubTab; label: string; icon: typeof ClipboardList }[] = [
    { key: 'mine', label: 'My tasks', icon: ClipboardList },
    { key: 'promises', label: 'Promises', icon: CalendarClock },
    { key: 'disputes', label: 'Disputes', icon: Flag },
];

export function FollowUpsTab({ items, canEdit, reloadKey, focusSub, focusNonce, onOpenDetail, onChanged }: Props) {
    const { toast } = useToast();
    const [sub, setSub] = useState<SubTab>('mine');

    useEffect(() => {
        if (focusNonce !== undefined && focusSub) setSub(focusSub);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [focusNonce]);
    const [myActions, setMyActions] = useState<ArAction[]>([]);
    const [loadingMine, setLoadingMine] = useState(true);

    const itemByKey = useMemo(() => new Map(items.map(i => [i.ItemKey, i])), [items]);
    const today = todayIso();

    const loadMine = useCallback(async () => {
        setLoadingMine(true);
        try {
            const res = await arDunningApi.listMyActions('Open');
            setMyActions(res.actions);
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to load your tasks', 'error');
        } finally {
            setLoadingMine(false);
        }
    }, [toast]);

    useEffect(() => { void loadMine(); }, [loadMine, reloadKey]);

    const resolve = async (a: ArAction, status: ArActionStatus) => {
        try {
            await arDunningApi.updateAction(a.Id, { status });
            await loadMine();
            onChanged();
        } catch (e) {
            toast(e instanceof Error ? e.message : 'Failed to update', 'error');
        }
    };

    const openItem = (key: string) => {
        const it = itemByKey.get(key);
        if (it) onOpenDetail(it);
        else toast('This item is no longer open (or outside your scope).', 'info');
    };

    const promises = useMemo(() => items.filter(i => i.HasOpenPromise).sort((a, b) => (a.PromiseDate ?? '').localeCompare(b.PromiseDate ?? '')), [items]);
    const disputes = useMemo(() => items.filter(i => i.IsDisputed).sort((a, b) => b.DaysOverdue - a.DaysOverdue), [items]);

    return (
        <div>
            <div className="mb-3 flex gap-1">
                {SUBTABS.map(s => {
                    const Icon = s.icon;
                    const active = sub === s.key;
                    return (
                        <button key={s.key} onClick={() => setSub(s.key)} className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ${active ? 'bg-indigo-100 text-indigo-700' : 'text-slate-500 hover:bg-slate-100'}`}>
                            <Icon className="h-4 w-4" /> {s.label}
                        </button>
                    );
                })}
            </div>

            {sub === 'mine' && (
                loadingMine ? (
                    <div className="py-10 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
                ) : myActions.length === 0 ? (
                    <Empty>No open tasks assigned to you.</Empty>
                ) : (
                    <div className="space-y-2">
                        {myActions.map(a => {
                            const it = itemByKey.get(a.EntityRef);
                            const dueRef = a.DueAt ?? a.PromiseDate;
                            const overdue = dueRef != null && dueRef < today;
                            return (
                                <div key={a.Id} className={`rounded-lg border p-3 text-sm ${overdue ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200 bg-white'}`}>
                                    <div className="flex items-center justify-between">
                                        <span className="flex items-center gap-2 font-medium text-slate-700">
                                            {a.ActionType === 'PromiseToPay' ? <CalendarClock className="h-4 w-4 text-sky-600" /> : <ClipboardList className="h-4 w-4 text-slate-400" />}
                                            {a.ActionType === 'PromiseToPay' ? 'Promise to pay' : 'Follow-up'}
                                            {dueRef && <span className={overdue ? 'text-rose-700' : 'text-slate-500'}>· {formatDate(dueRef)}{overdue ? ' (overdue)' : ''}</span>}
                                        </span>
                                        {canEdit && (
                                            <div className="flex gap-1">
                                                {a.ActionType === 'PromiseToPay' ? (
                                                    <>
                                                        <ResolveBtn onClick={() => resolve(a, 'Kept')} icon={Check} label="Kept" />
                                                        <ResolveBtn onClick={() => resolve(a, 'Broken')} icon={Ban} label="Broken" />
                                                    </>
                                                ) : (
                                                    <ResolveBtn onClick={() => resolve(a, 'Done')} icon={Check} label="Done" />
                                                )}
                                                <ResolveBtn onClick={() => resolve(a, 'Cancelled')} icon={Ban} label="Cancel" />
                                            </div>
                                        )}
                                    </div>
                                    <button onClick={() => openItem(a.EntityRef)} className="mt-1 block text-left text-slate-600 hover:text-indigo-700 hover:underline">
                                        {it ? `${it.InvoiceNo} · ${it.CustomerName ?? it.CustomerCode} · ${formatMoney(it.OpenAmountEUR)}` : a.EntityRef}
                                    </button>
                                    {a.Notes && <p className="mt-1 whitespace-pre-wrap text-xs text-slate-500">{a.Notes}</p>}
                                </div>
                            );
                        })}
                    </div>
                )
            )}

            {sub === 'promises' && (
                promises.length === 0 ? <Empty>No open promises-to-pay.</Empty> : (
                    <ItemList rows={promises} today={today} kind="promise" onOpen={onOpenDetail} />
                )
            )}
            {sub === 'disputes' && (
                disputes.length === 0 ? <Empty>No disputed / on-hold items.</Empty> : (
                    <ItemList rows={disputes} today={today} kind="dispute" onOpen={onOpenDetail} />
                )
            )}
        </div>
    );
}

function ItemList({ rows, today, kind, onOpen }: { rows: ArOpenItem[]; today: string; kind: 'promise' | 'dispute'; onOpen: (i: ArOpenItem) => void }) {
    return (
        <div className="space-y-2">
            {rows.map(i => {
                const overdue = kind === 'promise' && i.PromiseDate != null && i.PromiseDate < today;
                return (
                    <button key={i.ItemKey} onClick={() => onOpen(i)} className={`flex w-full items-center justify-between rounded-lg border p-3 text-left text-sm hover:border-indigo-300 ${overdue ? 'border-rose-200 bg-rose-50/50' : 'border-slate-200 bg-white'}`}>
                        <span className="min-w-0">
                            <span className="font-medium text-slate-700">{i.InvoiceNo}</span>
                            <span className="ml-2 text-slate-500">{i.CustomerName ?? i.CustomerCode}</span>
                            {kind === 'promise' && i.PromiseDate && <span className={`ml-2 ${overdue ? 'text-rose-700' : 'text-sky-700'}`}>promised {formatDate(i.PromiseDate)}{overdue ? ' (overdue)' : ''}</span>}
                            {kind === 'dispute' && <span className="ml-2 text-amber-700">{i.DaysOverdue}d overdue</span>}
                        </span>
                        <span className="ml-2 shrink-0 font-medium text-slate-800">{formatMoney(i.OpenAmountEUR)}</span>
                    </button>
                );
            })}
        </div>
    );
}

function ResolveBtn({ onClick, icon: Icon, label }: { onClick: () => void; icon: typeof Check; label: string }) {
    return (
        <button onClick={onClick} className="inline-flex items-center gap-1 rounded border border-slate-200 px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50">
            <Icon className="h-3 w-3" /> {label}
        </button>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">{children}</div>;
}
